/**
 * @file: TagOptionMenuLayer.tsx
 * @copyright: (c) 2019-2021 sichuan zhichetech co., ltd.
 */

import { memo, useCallback } from 'react';
import { LayoutChangeEvent } from 'react-native';
import Animated, {
  useAnimatedStyle,
  useDerivedValue,
  useSharedValue,
} from 'react-native-reanimated';
import { AnnotationTagOption } from '../functions/AnnotationTagOption';
import { TagInfo } from '../functions/TagInfo';
import { TagOptionMenu } from './TagOptionMenu';
import { tagAnchorPoint } from './TagView';

// the gap kept between the tag and its menu, and between the menu and the
// canvas edges.
const kMargin = 8;

// keeps the menu above every tag, whose z-indexes count up from zero.
const kZIndex = 10000;

/**
 * The option menu of the selected tag, hanging below it.
 *
 * It lives on the canvas rather than inside the tag view so that it is never
 * clipped by the tag's own bounds - on android a touch outside those bounds
 * would not reach it at all.
 */
export const TagOptionMenuLayer = memo(
  ({
    tag,
    scale = 1,
    canvasWidth,
    canvasHeight,
    options,
    onSelect,
  }: {
    tag: TagInfo;
    scale?: number;
    canvasWidth: number;
    canvasHeight: number;
    options: AnnotationTagOption[];
    onSelect?: (id: string, option: AnnotationTagOption) => void;
  }) => {
    const menuSize = useSharedValue({ width: 0, height: 0 });

    const onMenuLayout = useCallback(
      (e: LayoutChangeEvent) => {
        menuSize.value = {
          width: e.nativeEvent.layout.width,
          height: e.nativeEvent.layout.height,
        };
      },
      [menuSize],
    );

    const onOptionSelect = useCallback(
      (option: AnnotationTagOption) => {
        onSelect?.(tag.id, option);
      },
      [onSelect, tag.id],
    );

    const anchor = tagAnchorPoint(tag.height, scale);
    const { x, y, height: tagHeight } = tag;
    const translateX = tag.translateX;
    const translateY = tag.translateY;

    // the top-left corner of the tag view within the canvas.
    const tagLeft = useDerivedValue(
      () => x + (translateX ? translateX.value : 0) - anchor.x,
    );
    const tagTop = useDerivedValue(
      () => y + (translateY ? translateY.value : 0) - anchor.y,
    );

    const style = useAnimatedStyle(() => {
      const { width: menuWidth, height: menuHeight } = menuSize.value;
      return {
        // hidden until measured, otherwise it flashes at the wrong spot.
        opacity: menuHeight > 0 ? 1 : 0,
        transform: [
          {
            translateX: Math.max(
              kMargin,
              Math.min(tagLeft.value, canvasWidth - menuWidth - kMargin),
            ),
          },
          {
            translateY: Math.max(
              kMargin,
              Math.min(
                tagTop.value + tagHeight + kMargin,
                canvasHeight - menuHeight - kMargin,
              ),
            ),
          },
        ],
      };
    });

    return (
      <Animated.View
        onLayout={onMenuLayout}
        style={style}
        css={`
          position: absolute;
          left: 0px;
          top: 0px;
          z-index: ${kZIndex};
        `}
      >
        <TagOptionMenu
          options={options}
          selectedOptionId={tag.option?.id}
          onSelect={onOptionSelect}
        />
      </Animated.View>
    );
  },
);
